import { useRef, useState } from "react";
import { ArrowDown, CheckCircle2, Download, FileUp, Loader2, Lock, ScanLine, Sparkles, X, XCircle } from "lucide-react";
import { ACCEPT, MAX_FILE, markScanUsed, saveScanEmail, savedScan, scanResume } from "@/lib/resumeScan";
import { downloadFixPdf } from "@/lib/fixPdf";
import { planCost, planId, usePublicPlans } from "@/lib/plans";
import { EliteUpsell } from "@/components/login/ScanExtras";

const VERDICT = (s) => (s >= 80 ? ["Likely to pass", "text-emerald-400"] : s >= 60 ? ["Borderline", "text-[#F3C649]"] : ["At risk of being filtered", "text-red-400"]);

const ScoreRing = ({ v }) => {
  const r = 30, c = 2 * Math.PI * r;
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 72 72" className="h-20 w-20 -rotate-90">
        <circle cx="36" cy="36" r={r} stroke="#27272A" strokeWidth="6" fill="none" />
        <circle cx="36" cy="36" r={r} stroke="#D4AF37" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v / 100)} className="lp-ring" style={{ "--c": c }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-mono text-xl font-bold text-[#F3C649]" data-testid="scan-score">{v}</span>
    </div>
  );
};

const BeforeAfter = ({ rw }) => {
  if (!rw?.before || !rw?.after) return null;
  return (
    <div className="rounded-2xl border border-[#27272A] bg-[#09090B]/70 p-4" data-testid="scan-before-after">
      <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]"><Sparkles className="h-3.5 w-3.5" />AI rewrite of one of your bullets</div>
      <div className="rounded-xl border border-red-500/20 bg-red-500/[0.06] p-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-red-300">Before</span>
        <p className="mt-1 text-sm text-zinc-400 line-through decoration-red-400/50" data-testid="scan-before">{rw.before}</p>
      </div>
      <div className="my-1.5 flex justify-center"><ArrowDown className="h-4 w-4 text-[#D4AF37]" /></div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">After</span>
        <p className="mt-1 text-sm font-medium text-zinc-100" data-testid="scan-after">{rw.after}</p>
      </div>
      <p className="mt-2 text-[11px] text-zinc-500">Fill in any [brackets] with your real numbers. Elite rewrites every bullet like this, tailored to each job.</p>
    </div>
  );
};

const PdfButton = ({ r }) => {
  const plans = usePublicPlans();
  const [busy, setBusy] = useState(false);
  const elite = plans.find((p) => planId(p) === "elite");
  const go = async () => { setBusy(true); try { await downloadFixPdf(r, { elitePrice: elite ? planCost(elite, false).price : null }); } finally { setBusy(false); } };
  return (
    <button onClick={go} disabled={busy} data-testid="scan-pdf-button"
      className="flex w-full items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 px-5 py-2.5 text-sm font-medium text-[#F3C649] transition-colors hover:bg-[#33290D] disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download my fix list (PDF)
    </button>
  );
};

const EmailGate = ({ r, onUnlock }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const plans = usePublicPlans();
  const elite = plans.find((p) => planId(p) === "elite");
  const n = (r.checks?.length || 0) + (r.fixes?.length || 0);
  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em)) { setErr("Please enter a valid email."); return; }
    setBusy(true); setErr("");
    const res = await saveScanEmail({ ...r, email: em }, em, { elitePrice: elite ? planCost(elite, false).price : null });
    setBusy(false);
    if (res.invalid) { setErr(res.error); return; }
    onUnlock(em, res.ok ? (res.emailed ? "sent" : "skipped") : null);
  };
  return (
    <form onSubmit={submit} noValidate className="relative overflow-hidden rounded-2xl border border-[#27272A] bg-[#09090B]/70 p-4" data-testid="scan-email-gate">
      <div className="pointer-events-none mb-3 space-y-1.5 blur-[3px]" aria-hidden>{[70, 90, 55].map((w) => <div key={w} className="h-3 rounded bg-zinc-700/60" style={{ width: `${w}%` }} />)}</div>
      <p className="flex items-center gap-1.5 text-sm font-semibold text-zinc-100"><Lock className="h-4 w-4 text-[#D4AF37]" />See your full fix list{n ? ` (${n} items)` : ""}</p>
      <p className="mb-3 mt-0.5 text-xs text-zinc-500">We'll also email you a copy, so you can work through it later.</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" autoComplete="email" data-testid="scan-email-input"
          className="h-11 flex-1 rounded-full border border-[#27272A] bg-[#121215] px-4 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#D4AF37]/60 focus:outline-none" />
        <button type="submit" disabled={busy} className="h-11 rounded-full bg-[#D4AF37] px-5 text-sm font-semibold text-black transition-colors hover:bg-[#F3C649] disabled:opacity-60" data-testid="scan-email-submit">{busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Show my fixes"}</button>
      </div>
      {err && <p className="mt-2 text-xs text-red-400" data-testid="scan-email-error">{err}</p>}
      <p className="mt-2 text-[10px] text-zinc-600">No spam. Unsubscribe in one click.</p>
    </form>
  );
};

const ScanResult = ({ r: initial, onCta }) => {
  const [r, setR] = useState(initial);
  const [label, cls] = VERDICT(r.score);
  const unlock = (email, emailed) => { const next = { ...r, email, emailed }; markScanUsed(next); setR(next); };
  return (
    <div className="fade-up space-y-4" data-testid="scan-result">
      <div className="flex items-center gap-4 rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#33290D] to-[#141108] p-4">
        <ScoreRing v={r.score} />
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">{r.ai === false ? "Instant preview" : "ATS readiness"}</span>
          <p className={`font-display text-xl ${cls}`} data-testid="scan-verdict">{r.verdict || label}</p>
          {r.summary && <p className="mt-1 text-xs text-zinc-400">{r.summary}</p>}
        </div>
      </div>
      <BeforeAfter rw={r.rewrite} />
      <EliteUpsell score={r.score} />
      {!r.email ? <EmailGate r={r} onUnlock={unlock} /> : <>
      <div className="grid gap-2 sm:grid-cols-2">
        {r.checks?.map((c) => (
          <div key={c.label} className="flex gap-2 rounded-lg border border-[#27272A] bg-[#18181B] p-2.5 text-xs">
            {c.ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" /> : <XCircle className="h-4 w-4 shrink-0 text-red-400" />}
            <span><span className="text-zinc-200">{c.label}</span>{c.note && <span className="block text-zinc-500">{c.note}</span>}</span>
          </div>
        ))}
      </div>
      {r.fixes?.length > 0 && <ul className="space-y-1.5 text-sm text-zinc-300" data-testid="scan-fixes">{r.fixes.map((f) => <li key={f} className="flex gap-2"><span className="text-[#D4AF37]">→</span>{f}</li>)}</ul>}
      <PdfButton r={r} />
      {(r.emailed === "sent" || r.emailed === true) && <p className="text-center text-[11px] text-emerald-400" data-testid="scan-emailed-note">Your fix list PDF is on its way to {r.email}.</p>}
      {r.emailed === "skipped" && <p className="text-center text-[11px] text-zinc-500" data-testid="scan-emailed-skipped-note">We've already emailed this fix list. Use the button above to download another copy.</p>}
      </>}
      <button onClick={onCta} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#F3C649]" data-testid="scan-signup-button">
        <Lock className="h-4 w-4" />Fix my resume with AI, free for 7 days
      </button>
    </div>
  );
};

const ScanForm = ({ onDone }) => {
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const input = useRef(null);
  const ready = mode === "upload" ? !!file : text.trim().length >= 200;

  const pick = (f) => { setErr(""); if (!f) return; if (f.size > MAX_FILE) { setErr("Please choose a file under 5 MB."); return; } setFile(f); };
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await scanResume(mode === "upload" ? { file } : { text: text.trim() });
      markScanUsed(r); onDone(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-full border border-[#27272A] bg-[#09090B] p-1 text-xs">
        {[["upload", "Upload file"], ["paste", "Paste text"]].map(([m, l]) => (
          <button key={m} onClick={() => { setMode(m); setErr(""); }} data-testid={`scan-mode-${m}`}
            className={`rounded-full px-3.5 py-1.5 transition-colors ${mode === m ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
        ))}
      </div>
      {mode === "upload" ? (
        <div onClick={() => input.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D4AF37]/40 bg-[#09090B]/60 p-6 text-center transition-colors hover:border-[#D4AF37]/70" data-testid="scan-dropzone">
          <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="scan-file-input" />
          {file ? (
            <span className="flex items-center gap-2 text-sm text-zinc-200" data-testid="scan-file-name">{file.name}
              <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-zinc-500 hover:text-red-400" data-testid="scan-file-remove"><X className="h-4 w-4" /></button>
            </span>
          ) : (
            <><FileUp className="h-6 w-6 text-[#D4AF37]" /><span className="text-sm text-zinc-300">Drop your resume or tap to choose</span><span className="text-[11px] text-zinc-500">PDF, DOCX, DOC or TXT · up to 5 MB</span></>
          )}
        </div>
      ) : (
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 15000))} rows={6} placeholder="Paste your full resume text here…" data-testid="scan-text-input"
          className="w-full resize-none rounded-xl border border-[#27272A] bg-[#09090B] p-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#D4AF37]/60 focus:outline-none" />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[11px] text-zinc-500">Handled under our <a href={process.env.REACT_APP_PRIVACY_URL} target="_blank" rel="noreferrer" className="underline hover:text-[#F3C649]" data-testid="scan-privacy-link">Privacy Policy</a>.</span>
        <button onClick={submit} disabled={!ready || busy} data-testid="scan-submit-button"
          className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-[#F3C649] disabled:opacity-40">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Scanning like an ATS…</> : <><ScanLine className="h-4 w-4" />Scan my resume</>}
        </button>
      </div>
      {err && <p className="text-xs text-red-400" data-testid="scan-error">{err}</p>}
    </div>
  );
};

export const ResumeScan = ({ onCta }) => {
  const [done, setDone] = useState(savedScan);
  return (
    <div className="fade-up relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#121214]/90 p-6 shadow-[0_10px_35px_rgba(212,175,55,0.08)] backdrop-blur-xl sm:col-span-2" data-testid="resume-scan">
      <div className="pointer-events-none absolute -right-16 -bottom-16 h-44 w-44 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#D4AF37]/40 bg-[#33290D] text-[#F3C649]"><ScanLine className="h-5 w-5" /></span>
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F3C649]">Free resume scan · one try</span>
          <h3 className="font-display text-lg text-zinc-100">Would your resume pass the ATS Rating Filters?</h3>
        </div>
      </div>
      {done?.checks ? <ScanResult r={done} onCta={onCta} /> : <ScanForm onDone={setDone} />}
    </div>
  );
};
